import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import { IconLocationBroken } from '@tabler/icons-react';
import { Stack } from '@mantine/core';

import AppBanners from '~/domains/common/components/AppBanners/AppBanners';
import { PusherContextProvider } from '~/domains/pusher/PusherContext';
import { UnstyledButtonLink } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import SubHeader from '~/domains/common/components/AppShellHeader/components/Subheader/SubHeader';
import { ConnectedSignUpModal } from '~/domains/common/components/SignUpModal/ConnectedSignUpModal';
import MessageBanner, { BannerType } from '~/domains/common/components/MessageBanner/MessageBanner';
import { useIsMobile } from '~/domains/common/hooks/mediaQueries';
import Filters from '~/domains/common/components/Filters/Filters';
import dt from '~/testing';
import { useTreatment } from '~/domains/common/hooks/useTreatment';
import { FEATURE } from '~/common/enums/feature.enum';
import PromoCarousel from '~/domains/promotions/components/PromoCarousel/PromoCarousel';
import { PromoCarouselLocation } from '~/domains/promotions/types/enums';

import TableLobby from '../TableLobby/TableLobby';
import GridLobby from '../GridLobby/GridLobby';
import LobbyPagination from '../LobbyPagination';
import { ViewStyle } from '../../Lobby.enums';

import useStyles from './styles';
import { useLobby } from './useLobby';
import useLobbyFiltersProps from './useLobbyFilterProps';

function ConnectedLobby() {
  const isMobile = useIsMobile();
  const { classes, theme } = useStyles();
  const { t } = useTranslation('common');

  const {
    isAdmin,

    viewStyle,
    setLimit,
    page,
    pages,
    limit,
    itemsTotal,
    tabs,

    data,
    error,
    isLoading,
    isInitialLoading,
    hasRestrictedContests,
    isJurisdictionInfoEnabled,
    handlePageChange,
  } = useLobby();

  const filtersProps = useLobbyFiltersProps(isAdmin);

  // Check if Pusher is enabled via feature flag
  const { isReady, treatment } = useTreatment(FEATURE.CONFIG_PUSHER_ENABLED_ENDPOINTS);
  const isPusherEnabled = isReady && treatment?.treatment === 'on';

  return (
    <PusherContextProvider enabled={isPusherEnabled}>
      <SubHeader title={t('contestLobby.title')} subMenuItems={tabs} />
      <AppBanners />
      <section className={classes.layout}>
        <div className={classes.promoContainer}>
          <PromoCarousel
            location={PromoCarouselLocation.CONTEST}
            backgroundColor={theme.colors.gray2[0]}
          />
        </div>
        {isJurisdictionInfoEnabled && hasRestrictedContests && (
          <Stack mb={theme.other.spacing._16}>
            <MessageBanner
              type={BannerType.INFO}
              Icon={IconLocationBroken}
              title={t('contestLobby.locationInfo.title')}
            >
              <Text variant="body-medium" tag="div">
                <Trans
                  i18nKey="common:contestLobby.locationInfo.message"
                  components={{
                    link: (
                      <UnstyledButtonLink
                        href="https://splashsports.com/where-can-you-play"
                        target="_blank"
                      />
                    ),
                  }}
                />
              </Text>
            </MessageBanner>
          </Stack>
        )}
        <Filters {...filtersProps} />
        <div className={classes.lobbyWrapper}>
          {(() => {
            if (error) return null;

            return isMobile || viewStyle === ViewStyle.GRID ? (
              <GridLobby
                data-test-id={dt.components.lobby.grid}
                data={data?.data}
                isLoading={isInitialLoading}
              />
            ) : (
              <TableLobby
                data-test-id={dt.components.lobby.table}
                data={data?.data}
                isLoading={isInitialLoading}
                pagination={undefined}
              />
            );
          })()}
        </div>

        {!isLoading && !error && data?.data.length > 0 && (
          <LobbyPagination
            page={page}
            pages={pages}
            limit={limit}
            itemsTotal={itemsTotal}
            setLimit={setLimit}
            handlePageChange={handlePageChange}
          />
        )}
      </section>
      <ConnectedSignUpModal />
    </PusherContextProvider>
  );
}

export default ConnectedLobby;
